import env from "@/env.config";
import { axios } from "@/libraries/axios";
import cookie from "@/libraries/cookie";
import { isMpisAccount, isWebView } from "@/libraries/helper";
import {
	type AuthLoginSchema,
	AuthRegisterCredentialsSchema,
	type LoginSsoPayloadSchema,
} from "@/schemas";
import type { AuthSignInResponse } from "@/types";
import type {
	AxiosError,
	AxiosResponseHeaders,
	RawAxiosResponseHeaders,
} from "axios";

class AuthService {
	setToken(headers: RawAxiosResponseHeaders | AxiosResponseHeaders) {
		return new Promise((resolve) => {
			const isFromWebview = isWebView();
			const cookieOptions: Cookies.CookieAttributes = {
				expires: 20,
				secure: env.isSecureHttp && !isFromWebview,
			};
			cookie.set(env.authKeyToken, headers["access-token"], cookieOptions);
			cookie.set(env.authKeyClient, headers.client, cookieOptions);
			cookie.set(env.authKeyUid, headers.uid, cookieOptions);
			resolve(true);
		});
	}
	async clearToken() {
		const cacheNames = await caches.keys();
		await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
		return new Promise((resolve) => {
			cookie.remove(env.authKeyToken);
			cookie.remove(env.authKeyClient);
			cookie.remove(env.authKeyUid);
			cookie.remove(env.authKeyId);
			if (isMpisAccount()) {
				cookie.remove("account-type");
			}
			localStorage.clear();
			sessionStorage.clear();
			resolve(true);
		});
	}
	/**
	 * @description Login user
	 * @method POST
	 */
	async login(payload: AuthLoginSchema) {
		try {
			const res = await axios.post<AuthSignInResponse>(
				"/api/v1/auth/sign_in",
				payload,
			);
			await this.setToken(res.headers);
			return res.data;
		} catch (error) {
			return Promise.reject(error as AxiosError);
		}
	}
	/**
	 * @description Logout user
	 * @method GET
	 */
	async logout() {
		try {
			const res = await axios.get("/api/v1/auth/sign_out");
			await this.clearToken();
			return res.data;
		} catch (error) {
			return Promise.reject(error as AxiosError);
		}
	}
	/**
	 * @description Register user
	 * @method POST
	 */
	async forgotPassword(email: string) {
		try {
			const res = await axios.post("/api/v1/auth/password", { email });
			return res.data;
		} catch (error) {
			return Promise.reject(error as AxiosError);
		}
	}
	async register(
		payload: AuthRegisterCredentialsSchema & { cf_turnstile_token: string },
	) {
		try {
			const parsePayload = AuthRegisterCredentialsSchema.safeParse(payload);
			if (!parsePayload.success) return Promise.reject(parsePayload.error);
			const user = parsePayload.data;
			const res = await axios.post(
				"/api/v1/auth",
				{ user, cf_turnstile_token: payload.cf_turnstile_token },
				{ headers: { "X-Client-Type": "web" } },
			);
			await this.setToken(res.headers);
			return res.data;
		} catch (error) {
			return Promise.reject(error as AxiosError);
		}
	}
	async ssoLogin(payload: LoginSsoPayloadSchema) {
		try {
			const res = await axios.post<AuthSignInResponse>(
				"/api/v1/veins/login/sso",
				payload,
			);
			await this.setToken(res.headers);
			cookie.set("account-type", "sso");
			return res.data;
		} catch (error) {
			return Promise.reject(error as AxiosError);
		}
	}
}

export const authService = Object.freeze(new AuthService());
